import { createContext, useContext } from "react";

/**
 * Lets deeply-nested widgets (e.g. a wide table) drive the sidebar's expanded
 * state without prop-drilling through Messages → AssistantContent → WidgetOutput.
 * The sidebar provides the value; widgets read it to know whether they already
 * have the extra room and to request more when they don't.
 */
export interface PanelContextValue {
  /**
   * Whether the panel is currently WIDER than its default layout. Only true for a
   * horizontal expansion (requested by this context's `requestExpand`, e.g. a wide
   * table) — NOT when the panel is instead taller because the user hit the header's
   * expand button, which only ever grows height (see ai-assistant-sidebar.tsx). A wide
   * table's own "expand" affordance should stay visible whenever the width is still at
   * its default, regardless of whether the panel happens to be taller at the moment.
   */
  isWidthExpanded: boolean;
  /**
   * Ask the panel to switch to its wide layout. An optional `contentWidth` (the
   * natural pixel width of the widget that needs room, e.g. a table's scrollWidth)
   * lets the panel size itself to fit that content instead of a fixed width.
   */
  requestExpand: (contentWidth?: number) => void;
}

export const PanelContext = createContext<PanelContextValue>({
  isWidthExpanded: false,
  requestExpand: () => undefined,
});

/** Chrome around a widget inside the panel (panel padding + message row padding +
 * assistant avatar column + a little slack for the scrollbar). Added to a widget's
 * requested content width to get the panel width that shows it without scroll. */
export const PANEL_CHROME = 150;

export const usePanel = (): PanelContextValue => useContext(PanelContext);
